import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Star, MessageSquare, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StarBar = ({ rating, filled, total, onClick }) => (
  <div className="flex items-center gap-2 group" onClick={onClick} role={onClick ? 'button' : undefined}>
    <span className="text-xs w-4 text-[#525252]">{rating}★</span>
    <div className="flex-1 h-2 bg-[#F5F5F5] rounded-full overflow-hidden">
      <div
        className="h-full bg-yellow-400 transition-all"
        style={{ width: total ? `${(filled / total) * 100}%` : '0%' }}
      />
    </div>
    <span className="text-xs w-8 text-right text-[#737373]">{filled}</span>
  </div>
);

const StarPicker = ({ value, onChange, size = 'md' }) => {
  const [hover, setHover] = useState(0);
  const dim = size === 'lg' ? 'w-8 h-8' : 'w-5 h-5';
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((s) => (
        <button
          key={s}
          type="button"
          onMouseEnter={() => setHover(s)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(s)}
          className="transition-transform hover:scale-110"
          data-testid={`star-pick-${s}`}
          aria-label={`${s} estrellas`}
        >
          <Star
            className={`${dim} transition-colors ${
              (hover || value) >= s ? 'text-yellow-400 fill-yellow-400' : 'text-[#D4D4D4]'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

/**
 * EventReviews
 * - Loads summary (avg + distribution) and paginated reviews.
 * - Fetches eligibility to decide whether to show the write-review form.
 * - Handles create/edit inline.
 */
const EventReviews = ({ eventId }) => {
  const [summary, setSummary] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [eligibility, setEligibility] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        axios.get(`${API}/events/${eventId}/reviews/summary`),
        axios.get(`${API}/events/${eventId}/reviews?limit=25`),
      ]);
      setSummary(s.data);
      setReviews(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch {
      /* silent */
    }
    try {
      const e = await axios.get(`${API}/reviews/eligibility/${eventId}`, {
        withCredentials: true,
      });
      setEligibility(e.data);
      if (e.data?.existing_review) {
        setRating(e.data.existing_review.rating);
        setComment(e.data.existing_review.comment || '');
      }
    } catch {
      setEligibility(null); // Not logged in or 401 → hide form
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async () => {
    if (rating < 1 || rating > 5) {
      toast.error('Elegí entre 1 y 5 estrellas');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/events/${eventId}/reviews`,
        { rating, comment },
        { withCredentials: true }
      );
      toast.success(eligibility?.existing_review ? 'Reseña actualizada' : 'Gracias por tu reseña');
      setEditing(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar la reseña');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !summary) {
    return (
      <div className="py-8 text-center text-[#737373] text-sm" data-testid="reviews-loading">
        <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
        Cargando reseñas...
      </div>
    );
  }

  const canReview = eligibility?.can_review;
  const hasReview = !!eligibility?.existing_review;
  const showForm = canReview && (editing || !hasReview);

  return (
    <div className="space-y-6" data-testid="event-reviews">
      {/* Summary header */}
      {summary && summary.count > 0 ? (
        <div className="flex flex-col sm:flex-row sm:items-center sm:gap-8 gap-4 p-4 bg-white rounded-lg border border-[#E5E5E5]">
          <div className="text-center sm:text-left">
            <div className="text-5xl font-bold text-[#0A0A0A] leading-none" data-testid="reviews-avg">
              {summary.avg.toFixed(1)}
            </div>
            <div className="flex items-center justify-center sm:justify-start gap-0.5 mt-1">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star
                  key={s}
                  className={`w-4 h-4 ${
                    summary.avg >= s
                      ? 'text-yellow-400 fill-yellow-400'
                      : summary.avg >= s - 0.5
                      ? 'text-yellow-400 fill-yellow-400/60'
                      : 'text-[#D4D4D4]'
                  }`}
                />
              ))}
            </div>
            <div className="text-xs text-[#737373] mt-1">{summary.count} reseña{summary.count !== 1 ? 's' : ''}</div>
          </div>
          <div className="flex-1 space-y-1.5 max-w-md">
            {[5, 4, 3, 2, 1].map((r) => (
              <StarBar key={r} rating={r} filled={summary.distribution[r] || 0} total={summary.count} />
            ))}
          </div>
        </div>
      ) : (
        <div className="p-4 bg-white rounded-lg border border-[#E5E5E5] text-center">
          <MessageSquare className="w-8 h-8 text-[#B0B0B0] mx-auto mb-2" />
          <p className="text-sm text-[#737373]">Todavía no hay reseñas. ¡Sé el primero en dejar tu opinión!</p>
        </div>
      )}

      {/* Write / edit form */}
      {showForm && (
        <Card className="bg-white border-[#E5E5E5]" data-testid="reviews-form">
          <CardContent className="pt-5 pb-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#0A0A0A] mb-2">
                {hasReview ? 'Editá tu reseña' : '¿Cómo estuvo el evento?'}
              </label>
              <StarPicker value={rating} onChange={setRating} size="lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0A0A0A] mb-1">Tu opinión (opcional)</label>
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="¿Qué te gustó? ¿Qué mejorarías?"
                maxLength={1000}
                rows={3}
                className="bg-white border-[#E5E5E5]"
                data-testid="reviews-comment"
              />
              <div className="text-xs text-[#737373] text-right mt-1">{comment.length}/1000</div>
            </div>
            <div className="flex gap-2">
              {hasReview && (
                <Button variant="outline" onClick={() => setEditing(false)} className="border-[#E5E5E5]">Cancelar</Button>
              )}
              <Button
                onClick={submit}
                disabled={submitting || rating < 1}
                className="bg-primary hover:bg-primary/90 text-white font-medium"
                data-testid="reviews-submit"
              >
                {submitting ? 'Enviando...' : hasReview ? 'Actualizar reseña' : 'Publicar reseña'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Existing review chip (if not editing) */}
      {hasReview && !editing && (
        <div className="flex items-center justify-between p-3 bg-[#FFF7ED] border border-orange-200 rounded-lg text-sm" data-testid="reviews-existing-chip">
          <div>
            <span className="font-medium text-[#0A0A0A]">Ya dejaste tu reseña</span>
            <span className="text-[#737373]"> · {eligibility.existing_review.rating}★</span>
          </div>
          <Button size="sm" variant="ghost" onClick={() => setEditing(true)} className="text-primary hover:bg-white/60">Editar</Button>
        </div>
      )}

      {eligibility && !eligibility.can_review && eligibility.reason && (
        <div className="text-xs text-[#737373] italic px-1">
          {eligibility.reason === 'event_not_started' && 'Vas a poder dejar tu reseña después de que arranque el evento.'}
          {eligibility.reason === 'no_rsvp' && 'Sólo pueden reseñar quienes hayan reservado.'}
          {eligibility.reason === 'payment_pending' && 'Cuando se confirme tu pago vas a poder dejar tu reseña.'}
        </div>
      )}

      {/* Reviews list */}
      {reviews.length > 0 && (
        <div className="space-y-3" data-testid="reviews-list">
          {reviews.map((r) => (
            <Card key={r.review_id} className="bg-white border-[#E5E5E5]" data-testid={`review-${r.review_id}`}>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    {r.user_picture ? (
                      <img src={r.user_picture} alt="" className="w-9 h-9 rounded-full object-cover" />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-sm">
                        {(r.user_name || '?').slice(0, 1).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <div className="font-medium text-sm text-[#0A0A0A]">{r.user_name || 'Anónimo'}</div>
                      <div className="text-xs text-[#737373]">
                        {r.created_at ? format(new Date(r.created_at), "d MMM yyyy", { locale: es }) : ''}
                        {r.updated_at && ' · editada'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star key={s} className={`w-4 h-4 ${r.rating >= s ? 'text-yellow-400 fill-yellow-400' : 'text-[#D4D4D4]'}`} />
                    ))}
                  </div>
                </div>
                {r.comment && <p className="mt-3 text-sm text-[#0A0A0A] whitespace-pre-line">{r.comment}</p>}
              </CardContent>
            </Card>
          ))}
          {reviews.length < total && (
            <p className="text-center text-xs text-[#737373] py-2">Mostrando {reviews.length} de {total} reseñas.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default EventReviews;
